import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  CompanyLocation,
  getBase64,
  updateLocation,
  UpdateLocationDto,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface EditLocationProps extends ModalProps {
  companyLocation?: CompanyLocation;
  companyId: string;
}

export function EditLocationModal({
  companyLocation,
  companyId,
  ...props
}: EditLocationProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [avatarPhoto, setAvatarPhoto] = useState<File | undefined>(undefined);
  const [currentAvatarPhoto, setCurrentAvatarPhoto] = useState<
    string | undefined
  >(companyLocation?.avatarPhoto);

  const updateLocationSchema = z.object({
    code: z.number().min(1, t("location.Code is required")),
    name: z.string().min(1, t("common.Name is required")),
    email: z.string().email(t("location.Invalid email format")),
    phone: z.string().min(1, t("common.Phone number is required")),
    address: z.string().min(1, t("common.Address is required")),
  });
  type UpdateLocationData = z.infer<typeof updateLocationSchema>;
  const {
    handleSubmit,
    formState: state,
    reset,
    register,
  } = useForm<UpdateLocationData>({
    resolver: zodResolver(updateLocationSchema),
  });

  const mutation = useMutation({
    mutationFn: updateLocation,
    onSuccess: () => {
      props.onClose?.();
      toast.success(t("common.The changes have been saved"));
      queryClient.invalidateQueries({ queryKey: ["allLocations", companyId] });
    },
    onError: () => {
      toast.error(t("common.Unable to save the changes"));
    },
  });

  useEffect(() => {
    setCurrentAvatarPhoto(companyLocation?.avatarPhoto);
  }, [companyLocation]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setAvatarPhoto(e.target.files?.[0]);
    const avatarBase64 = await getBase64(
      e.target.files?.[0] as unknown as File,
    );
    setCurrentAvatarPhoto(avatarBase64);
  };

  const onSubmit = async (data: UpdateLocationData) => {
    let photo = "";
    if (avatarPhoto) {
      photo = await getBase64(avatarPhoto as unknown as File);
    }
    const payload: UpdateLocationDto = {
      id: companyLocation?.id ?? "",
      name: data.name,
      code: data.code,
      email: data.email,
      phone: data.phone,
      avatarPhoto: photo ? photo : currentAvatarPhoto,
      address: data.address,
      companyId: companyId,
    };

    mutation.mutate(payload);
  };

  useEffect(() => {
    reset(companyLocation);
  }, [companyLocation, reset]);

  return (
    <Modal
      {...props}
      title={t("location.Edit Location")}
      actionButtons={
        <>
          <Button
            type="button"
            disabled={mutation.isPending}
            onClick={props?.onClose}
            variant="secondary"
          >
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("location.Save Location")}
          </Button>
        </>
      }
    >
      <form>
        <label className="text-gray-900 text-[15px] font-[600]">
          {t("location.General Information")}
        </label>
        <div className="flex flex-col">
          <Label title={t("location.Location Name")} required>
            <Input
              {...register("name")}
              name="name"
              size="sm"
              defaultValue={companyLocation?.name}
            />
          </Label>
          <ErrorMessage message={state.errors.name?.message} />
        </div>
        <div className="flex flex-col">
          <Label title={t("location.Location CVR")} required>
            <Input
              {...register("code", { valueAsNumber: true })}
              name="code"
              size="sm"
              defaultValue={companyLocation?.code}
            />
          </Label>
          <ErrorMessage message={state.errors.code?.message} />
        </div>
        <div className="flex flex-col">
          <Label title={t("location.Location Email")} required>
            <Input
              {...register("email")}
              name="email"
              size="sm"
              defaultValue={companyLocation?.email}
            />
          </Label>
          <ErrorMessage message={state.errors.email?.message} />
        </div>
        <div className="flex flex-col">
          <Label title={t("location.Location Phone")} required>
            <Input
              {...register("phone")}
              name="phone"
              type="text"
              size="sm"
              defaultValue={companyLocation?.phone}
            />
          </Label>
          <ErrorMessage message={state.errors.phone?.message} />
        </div>
        <div className="flex flex-col">
          <Label title={t("location.Location Address")} required>
            <Input
              {...register("address")}
              name="address"
              size="sm"
              defaultValue={companyLocation?.address}
            />
          </Label>
          <ErrorMessage message={state.errors.address?.message} />
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex items-center">
            <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
              {t("location.Location Avatar")}
            </label>
          </div>
          <div className="flex flex-row justify-between w-[356px] relative">
            <div className="py-2 text-gray-700 font-normal min-w-32 text-2sm flex items-center">
              150x150px JPEG, PNG Image
            </div>
            <div className=" rounded-full overflow-hidden size-16 relative">
              <div className="flex justify-center items-center">
                <div className=" rounded-full overflow-hidden size-16 relative">
                  <input
                    className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".png, .jpg, .jpeg"
                    name="avatar"
                    type="file"
                    onChange={handleAvatarChange}
                  />
                  <div
                    className="rounded-full w-full h-full"
                    style={{
                      backgroundImage: `url(${currentAvatarPhoto})`,
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                    }}
                  ></div>
                  <div className="flex items-center justify-center cursor-pointer h-5 left-0 right-0 bottom-0 bg-dark-clarity absolute">
                    <svg
                      className="fill-light opacity-80"
                      height="12"
                      viewBox="0 0 14 12"
                      width="14"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.6665 2.64585H11.2232C11.0873 2.64749 10.9538 2.61053 10.8382 2.53928C10.7225 2.46803 10.6295 2.36541 10.5698 2.24335L10.0448 1.19918C9.91266 0.931853 9.70808 0.707007 9.45438 0.550249C9.20068 0.393491 8.90806 0.311121 8.60984 0.312517H5.38984C5.09162 0.311121 4.799 0.393491 4.5453 0.550249C4.2916 0.707007 4.08701 0.931853 3.95484 1.19918L3.42984 2.24335C3.37021 2.36541 3.27716 2.46803 3.1615 2.53928C3.04584 2.61053 2.91234 2.64749 2.7765 2.64585H2.33317C1.90772 2.64585 1.49969 2.81486 1.19885 3.1157C0.898014 3.41654 0.729004 3.82457 0.729004 4.25002V10.0834C0.729004 10.5088 0.898014 10.9168 1.19885 11.2177C1.49969 11.5185 1.90772 11.6875 2.33317 11.6875H11.6665C12.092 11.6875 12.5 11.5185 12.8008 11.2177C13.1017 10.9168 13.2707 10.5088 13.2707 10.0834V4.25002C13.2707 3.82457 13.1017 3.41654 12.8008 3.1157C12.5 2.81486 12.092 2.64585 11.6665 2.64585ZM6.99984 9.64585C6.39413 9.64585 5.80203 9.46624 5.2984 9.12973C4.79478 8.79321 4.40225 8.31492 4.17046 7.75532C3.93866 7.19572 3.87802 6.57995 3.99618 5.98589C4.11435 5.39182 4.40602 4.84613 4.83432 4.41784C5.26262 3.98954 5.80831 3.69786 6.40237 3.5797C6.99644 3.46153 7.61221 3.52218 8.1718 3.75397C8.7314 3.98576 9.2097 4.37829 9.54621 4.88192C9.88272 5.38554 10.0623 5.97765 10.0623 6.58335C10.0608 7.3951 9.73765 8.17317 9.16365 8.74716C8.58965 9.32116 7.81159 9.64431 6.99984 9.64585Z"
                        fill=""
                      ></path>
                      <path
                        d="M7 8.77087C8.20812 8.77087 9.1875 7.7915 9.1875 6.58337C9.1875 5.37525 8.20812 4.39587 7 4.39587C5.79188 4.39587 4.8125 5.37525 4.8125 6.58337C4.8125 7.7915 5.79188 8.77087 7 8.77087Z"
                        fill=""
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
